import {useCallback, useEffect, useMemo, useState} from 'react';
import {format} from 'date-fns';
import {QueryFunctionContext, useMutation, useQuery} from 'react-query';

import Typography from 'components/Typography';
import StatusBadge from 'components/StatusBadge';
import ViewDetails from 'components/ViewDetails';
import styles from './Requests.module.scss';
import {
  Address,
  Customer,
  DataItem,
  DataTableSchemaItem,
  ListResponse,
  OrderType,
  Product,
  Request,
  SelectOption,
  SyncSource,
} from 'types';
import useSort from 'hooks/useSort';
import api from 'api';
import {
  mapStatusColor,
  mapStatusText,
  mapMMStatusText,
  mapMMSubStatusColor,
} from 'constants/mappings';
import {get, reject, sortBy as sortLodash} from 'lodash';
import {FilterOption} from 'pages/DeliveryPlanner/components/Filter/Filter';
import {useHistory, useRouteMatch} from 'react-router-dom';
import toast from 'react-hot-toast';
import CheckIcon from './CheckIcon';
import classNames from 'classnames';
import {formatPrice} from 'utils/format-price';
import {SingleValue} from 'react-select';
import {CompanyProperty} from 'types/property';
import useAuth from 'hooks/useAuth';
import useCompanyConfig from 'hooks/useCompanyConfig';

async function getRequests({queryKey}: QueryFunctionContext<any[]>) {
  const [
    ,
    page,
    order,
    status,
    userId,
    keyWord,
    source,
    date,
    location,
    preference_time,
    onlyOTC,
    paid,
    priority,
    createdDate,
    attemptStart,
    attemptEnd,
    landlord,
    propertyName,
    propertyAddress,
    syncSource,
  ] = queryKey;

  const {data} = await api.get<ListResponse<Request>>('/requests', {
    params: {
      order: order,
      status: status ? status : undefined,
      user_id: userId,
      offset: +page * 20,
      search: keyWord,
      limit: 40,
      source: source,
      preference_date: date ? format(date, 'yyyy-MM-dd') : undefined,
      zip_code:
        location.length > 0
          ? location.reduce((pr: [], curr: any) => [...pr, curr.label], [])
          : undefined,
      preference_time: preference_time ? preference_time.value : undefined,
      priority: priority ? priority.value : undefined,
      offering_type: onlyOTC ? onlyOTC.value : undefined,
      is_paid: paid ? paid.value === 'true' : undefined,
      attempt_start: attemptStart ? attemptStart.value : undefined,
      attempt_end: attemptEnd ? attemptEnd.value : undefined,
      landlord_id: landlord ? landlord.value : undefined,
      property_name: propertyName ? propertyName.value : undefined,
      property_id: propertyAddress ? propertyAddress.value : undefined,
      start_date: createdDate.start
        ? format(createdDate.start, 'yyyy-MM-dd')
        : undefined,
      end_date: createdDate.end
        ? format(createdDate.end, 'yyyy-MM-dd')
        : undefined,
      sync_source: syncSource ? syncSource.value : undefined,
    },
  });

  return data;
}

async function getLandlordList() {
  const {data} = await api.get<ListResponse<CompanyProperty['landlord']>>(
    '/users/',
    {
      params: {role: 'landlord'},
    }
  );

  return data;
}

async function getCompanyProperties({queryKey}: QueryFunctionContext<any[]>) {
  const [, page] = queryKey;
  const {data} = await api.get<ListResponse<CompanyProperty>>(`/properties/`, {
    params: {
      limit: 20,
      page: +page * 20,
    },
  });

  return data;
}

async function submitOrderStatusesAsync(body: any) {
  return api.post('/requests/requests/bulk-update/', body);
}
const DEFAULT_ORDERS_SCHEME: DataTableSchemaItem[] = [
  {
    dataKey: 'checkbox',
    header: <div style={{height: 40}}></div>,
    sortable: false,
    colWidth: '60px',
    index: -1,
  },

  {
    dataKey: 'number',
    header: 'Order #',
    sortable: true,
    index: 0,
  },

  {
    dataKey: 'customer',
    header: 'Customer',
    sortable: true,
    index: 2,
  },
  {dataKey: 'status', header: 'Status', sortable: true, index: 3},
  {
    dataKey: 'drug_name',
    header: 'Offering',
    sortable: false,
    index: 4,
  },
  {dataKey: 'amount_due', header: 'Amount', sortable: false, index: 5},
  {
    dataKey: 'sync_time',
    header: 'Date/Time',
    sortable: true,
    align: 'center',
    index: 6,
  },
  {
    dataKey: 'priority',
    header: 'Priority',
    sortable: true,
    align: 'center',
    index: 8,
  },
  {
    dataKey: 'delivery_address',
    header: 'Address',
    sortable: false,
    align: 'center',
    index: 9,
  },
  {
    dataKey: 'attempt',
    header: 'Attempt',
    sortable: true,
    align: 'center',
    index: 10,
  },
  {
    dataKey: 'preference_date',
    header: 'Preference Date',
    sortable: false,
    index: 11,
  },
];

const PICKUP_DROPOFF_ORDERS_SCHEME: DataTableSchemaItem[] = [
  {
    dataKey: 'checkbox',
    header: <div style={{height: 40}}></div>,
    sortable: false,
    colWidth: '80px',
    index: -1,
  },

  {
    dataKey: 'number',
    header: 'Order #',
    sortable: true,
    index: 0,
  },
  {
    dataKey: 'order_type',
    header: 'Order Type',
    sortable: false,
    index: 1,
  },
  {
    dataKey: 'customer',
    header: 'Customer',
    sortable: true,
    index: 2,
  },
  {dataKey: 'status', header: 'Status', sortable: true, index: 3},
  {
    dataKey: 'drug_name',
    header: 'Offering',
    sortable: false,
    index: 4,
  },
  {dataKey: 'amount_due', header: 'Amount', sortable: false, index: 5},
  {
    dataKey: 'sync_time',
    header: 'Date/Time',
    sortable: true,
    align: 'center',
    index: 6,
  },
  {
    dataKey: 'priority',
    header: 'Priority',
    sortable: true,
    align: 'center',
    index: 8,
  },
  {
    dataKey: 'delivery_address',
    header: 'Address',
    sortable: false,
    align: 'center',
    index: 9,
  },
  {
    dataKey: 'attempt',
    header: 'Attempt',
    sortable: true,
    align: 'center',
    index: 10,
  },
  {
    dataKey: 'preference_date',
    header: 'Preference Date',
    sortable: false,
    index: 11,
  },
];
const disabledStatus = ['canceled', 'completed', 'archived'];

type RequestFilter = {
  userId?: string;
  source?: string;
  page?: number;
};

function useRequests(props: RequestFilter) {
  const history = useHistory();
  const {hasFeature, isPickupDropoff} = useCompanyConfig();
  const {params} = useRouteMatch<{page: string | undefined}>();
  const {sortBy, sortOrder, order, onSort} = useSort('id', 'desc');
  const [source] = useState(props.source || '');
  const [status, setStatus] = useState(
    sessionStorage.getItem('state') === 'all'
      ? ''
      : sessionStorage.getItem(
          source === 'app' ? 'requests_state' : 'orders_state'
        ) || ''
  );
  const [selectedOrderType, setSelectedOrderType] = useState<
    OrderType | undefined
  >(undefined);
  const {currentUser} = useAuth();
  const [key, setKeyWord] = useState(
    sessionStorage.getItem(`searchKey_${source}`) || ''
  );
  const [keyWord, setApiKeyWord] = useState(
    sessionStorage.getItem(`searchKey_${source}`) || ''
  );
  const [location, setLocation] = useState<FilterOption[]>([]);
  const [jobDescription, setJobDescription] = useState('');
  const initialDate = sessionStorage.getItem('date')
    ? new Date(JSON.parse(sessionStorage.getItem('date') || ''))
    : undefined;

  const [date, setDate] = useState(initialDate);

  const initialTime = sessionStorage.getItem('preferenceTime')
    ? JSON.parse(sessionStorage.getItem('preferenceTime') || '')
    : undefined;

  const initialPriority = sessionStorage.getItem('priority')
    ? JSON.parse(sessionStorage.getItem('priority') || '')
    : undefined;

  const [preferenceTime, setPreferenceTime] = useState<
    undefined | {value: string; label: string}
  >(initialTime);

  const [priority, setPriority] = useState<
    undefined | {value: string; label: string}
  >(initialPriority);

  const initialOTC = sessionStorage.getItem('onlyOTC')
    ? JSON.parse(sessionStorage.getItem('onlyOTC') || '')
    : undefined;

  const [onlyOTC, setOnlyOTC] = useState<
    undefined | {value: string; label: string}
  >(initialOTC);

  const initialPaid = sessionStorage.getItem('paid')
    ? JSON.parse(sessionStorage.getItem('paid') || '')
    : undefined;
  const initialCreatedDate = sessionStorage.getItem('createdDate')
    ? {
        start: new Date(
          JSON.parse(sessionStorage.getItem('createdDate') || '').start
        ),
        end: new Date(
          JSON.parse(sessionStorage.getItem('createdDate') || '').end
        ),
      }
    : {
        start: undefined,
        end: undefined,
      };

  const [paid, setPaid] = useState<undefined | {value: string; label: string}>(
    initialPaid
  );
  const initialAttemptStart = sessionStorage.getItem('attempt_start')
    ? JSON.parse(sessionStorage.getItem('attempt_start') || '')
    : undefined;
  const initialAttemptEnd = sessionStorage.getItem('attempt_end')
    ? JSON.parse(sessionStorage.getItem('attempt_end') || '')
    : undefined;
  const [attemptStart, setAttemptStart] = useState<
    undefined | {value: string; label: string}
  >(initialAttemptStart);
  const [attemptEnd, setAttemptEnd] = useState<
    undefined | {value: string; label: string}
  >(initialAttemptEnd);
  const initialPropertyName = sessionStorage.getItem('property_name')
    ? JSON.parse(sessionStorage.getItem('property_name') || '')
    : undefined;
  const initialPropertyAddress = sessionStorage.getItem('property_address')
    ? JSON.parse(sessionStorage.getItem('property_address') || '')
    : undefined;
  const initialLandlord = sessionStorage.getItem('filter_landlord')
    ? JSON.parse(sessionStorage.getItem('filter_landlord') || '')
    : undefined;

  const [propertyName, setPropertyName] = useState<
    undefined | {value: string; label: string}
  >(initialPropertyName);
  const [propertyAddress, setPropertyAddress] = useState<
    undefined | {value: string; label: string}
  >(initialPropertyAddress);
  const [landlord, setLandlord] = useState<
    undefined | {value: string; label: string}
  >(initialLandlord);
  const initialSyncSource = sessionStorage.getItem('sync_source')
    ? JSON.parse(sessionStorage.getItem('sync_source') || '')
    : undefined;
  const [syncSource, setSyncSource] = useState<
    undefined | {value: SyncSource; label: string}
  >(initialSyncSource);
  const [createdDate, setCreatedDate] = useState(initialCreatedDate);
  const [clients, setClients] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [services, setServices] = useState<Product[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [MMTable, setMMTable] = useState(
    isPickupDropoff
      ? PICKUP_DROPOFF_ORDERS_SCHEME.map((item) => ({
          ...item,
          value: item.dataKey,
          label: item.header,
        }))
      : DEFAULT_ORDERS_SCHEME.map((item) => ({
          ...item,
          value: item.dataKey,
          label: item.header,
        }))
  );
  const [currentPage, setCurrentPage] = useState(parseInt(params.page || '1'));
  const [submitting, setSubmitting] = useState(false);
  const [productIds, setProductIds] = useState<any>([]);
  const [serviceIds, setServiceIds] = useState<any>([]);
  const [userId, setUserId] = useState<any>(0);
  const [addressId, setAddressId] = useState<any>(0);
  const [visible, setVisible] = useState(false);
  const [filter, setFilter] = useState<any>({
    location: [],
    priority: sessionStorage.getItem('priority')
      ? JSON.parse(sessionStorage.getItem('priority') || '')
      : undefined,
    paid: sessionStorage.getItem('paid')
      ? JSON.parse(sessionStorage.getItem('paid') || '')
      : undefined,
    statuses: sessionStorage.getItem(`request_statuses`)
      ? JSON.parse(sessionStorage.getItem(`request_statuses`) as any)
      : [],
    types: sessionStorage.getItem(`request_types`)
      ? JSON.parse(sessionStorage.getItem(`request_types`) as any)
      : undefined,
    onlyOTC: sessionStorage.getItem(`onlyOTC`)
      ? JSON.parse(sessionStorage.getItem(`onlyOTC`) as any)
      : undefined,
    date: sessionStorage.getItem('date')
      ? new Date(JSON.parse(sessionStorage.getItem('date') || ''))
      : undefined,
    preferenceTime: sessionStorage.getItem('preferenceTime')
      ? JSON.parse(sessionStorage.getItem('preferenceTime') || '')
      : undefined,
    createdDate: initialCreatedDate,
    attempt_start: sessionStorage.getItem('attempt_start')
      ? JSON.parse(sessionStorage.getItem('attempt_start') || '')
      : undefined,
    attempt_end: sessionStorage.getItem('attempt_end')
      ? JSON.parse(sessionStorage.getItem('attempt_end') || '')
      : undefined,
    property_address: sessionStorage.getItem('property_address')
      ? JSON.parse(sessionStorage.getItem('property_address') || '')
      : undefined,
    landlord: sessionStorage.getItem('filter_landlord')
      ? JSON.parse(sessionStorage.getItem('filter_landlord') || '')
      : undefined,
    property_name: sessionStorage.getItem('property_name')
      ? JSON.parse(sessionStorage.getItem('property_name') || '')
      : undefined,
    sync_source: sessionStorage.getItem('sync_source')
      ? JSON.parse(sessionStorage.getItem('sync_source') || '')
      : undefined,
  });
  const [filterVisible, setFilterVisible] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [addressVisible, setAddressVisible] = useState(false);
  const [productVisible, setProductVisible] = useState(false);
  const [serviceVisible, setServiceVisible] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
  const [orderStatus, setOrderStatus] = useState('ready_for_dispatch');
  const [statusesData, setStatusesData] = useState<{
    preference_time: null | string;
    preference_date: null | Date;
    priority: null | string;
  }>({
    preference_time: null,
    preference_date: null,
    priority: null,
  });
  const [isMerging, setIsMerging] = useState(false);

  function renderOrderTypeBadge(orderType: OrderType | undefined) {
    if (orderType !== 'dropoff' && orderType !== 'pickup') return null;
    return (
      <StatusBadge
        color={orderType === 'dropoff' ? 'green' : 'default'}
        className={styles.orderTypeBadge}
      >
        {orderType === 'dropoff' ? 'C' : 'P'}
      </StatusBadge>
    );
  }

  function transformData(data: Request[], source: string) {
    return data.map((item) => ({
      ...item,
      order_type: renderOrderTypeBadge(item.order_type),
      sync_time: get(item, 'sync_date', '') + '\n' + item.sync_time,
      amount_due: formatPrice(get(item, 'amount_due', 0)),
      priority: item.priority
        ? item.priority?.[0]?.toUpperCase() + item.priority?.slice(1)
        : '',
      number: (
        <Typography variant="strong">
          #{item.sequential_order_number}
        </Typography>
      ),
      preference_date: item.preference_date
        ? `${item.preference_date}${
            item.preference_time ? ` / ${item.preference_time}` : ''
          }`
        : '',

      status:
        source === 'micromerchant' ? (
          // @ts-ignore
          <StatusBadge color={mapMMSubStatusColor[item.status]}>
            {/*// @ts-ignore*/}
            {mapMMStatusText[item.status]}
          </StatusBadge>
        ) : (
          <StatusBadge color={mapStatusColor[item.status]}>
            {/*@ts-ignore*/}
            {mapStatusText[item.status]}
          </StatusBadge>
        ),
      checkbox: (
        <div
          className={classNames(styles.checkbox, {
            [styles['--disabled']]: disabledStatus.includes(item.status),
          })}
          onClick={(val) => {
            val.stopPropagation();
            if (disabledStatus.includes(item.status)) {
              return;
            }
            const include = selectedOrders.find((i) => i === item.id);

            if (include) {
              setSelectedOrders(selectedOrders.filter((i) => i !== item.id));
            } else {
              setSelectedOrders((pr) => [...pr, item.id]);
            }
          }}
        >
          <div className={styles.tooltip}>You can't select closed orders</div>
          <CheckIcon
            emptySelect={!selectedOrders.find((i) => i === item.id)}
            allSelected={!!selectedOrders.find((i) => i === item.id)}
          />
        </div>
      ),
      view: <ViewDetails to={`/requests/${item.id}`} />,
    }));
  }

  const getLocalTable = () => {
    let localTable = localStorage.getItem('tableData');
    if (localTable) {
      const data: any[] = isPickupDropoff
        ? PICKUP_DROPOFF_ORDERS_SCHEME.filter((item: DataTableSchemaItem) => {
            if (item.dataKey === 'checkbox') {
              return true;
            }
            return JSON.parse(localTable || '').find(
              (el: DataTableSchemaItem) => el.dataKey === item.dataKey
            );
          })
        : DEFAULT_ORDERS_SCHEME.filter((item: DataTableSchemaItem) => {
            if (item.dataKey === 'checkbox') {
              return true;
            }
            return JSON.parse(localTable || '').find(
              (el: DataTableSchemaItem) => el.dataKey === item.dataKey
            );
          });
      setMMTable(
        data.map((item) => ({...item, value: item.dataKey, label: item.header}))
      );
    }
  };

  useEffect(() => {
    if (isPickupDropoff) {
      setMMTable(
        PICKUP_DROPOFF_ORDERS_SCHEME.map((item) => ({
          ...item,
          value: item.dataKey,
          label: item.header,
        }))
      );
    } else {
      setMMTable(
        DEFAULT_ORDERS_SCHEME.map((item) => ({
          ...item,
          value: item.dataKey,
          label: item.header,
        }))
      );
    }
  }, [isPickupDropoff]);

  useEffect(() => {
    if (params.page) {
      setCurrentPage(parseInt(params.page));
    }
  }, [params.page]);

  useEffect(() => {
    const controller = new AbortController();
    const {signal} = controller;
    getLocalTable();
    getClients(signal);
    getProducts(signal);
    return () => controller.abort();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeOutId = setTimeout(() => {
      setApiKeyWord(key);
    }, 500);
    return () => clearTimeout(timeOutId);
  }, [key]);

  useEffect(() => {
    sessionStorage.setItem('location', JSON.stringify(location));
  }, [location]);

  useEffect(() => {
    if (paid) {
      sessionStorage.setItem('paid', JSON.stringify(paid));
    } else {
      sessionStorage.setItem('paid', '');
    }
    if (priority) {
      sessionStorage.setItem('priority', JSON.stringify(priority));
    } else {
      sessionStorage.setItem('priority', '');
    }
    if (onlyOTC) {
      sessionStorage.setItem('onlyOTC', JSON.stringify(onlyOTC));
    } else {
      sessionStorage.setItem('onlyOTC', '');
    }
    if (preferenceTime) {
      sessionStorage.setItem('preferenceTime', JSON.stringify(preferenceTime));
    } else {
      sessionStorage.setItem('preferenceTime', '');
    }
    if (priority) {
      sessionStorage.setItem('priority', JSON.stringify(priority));
    } else {
      sessionStorage.setItem('priority', '');
    }
    if (date) {
      sessionStorage.setItem('date', JSON.stringify(date));
    } else {
      sessionStorage.setItem('date', '');
    }
    if (attemptStart) {
      sessionStorage.setItem('attempt_start', JSON.stringify(attemptStart));
    } else {
      sessionStorage.setItem('attempt_start', '');
    }
    if (attemptEnd) {
      sessionStorage.setItem('attempt_end', JSON.stringify(attemptEnd));
    } else {
      sessionStorage.setItem('attempt_end', '');
    }
    if (landlord) {
      sessionStorage.setItem('filter_landlord', JSON.stringify(landlord));
    } else {
      sessionStorage.setItem('filter_landlord', '');
    }
    if (propertyAddress) {
      sessionStorage.setItem(
        'property_address',
        JSON.stringify(propertyAddress)
      );
    } else {
      sessionStorage.setItem('property_address', '');
    }
    if (propertyName) {
      sessionStorage.setItem('property_name', JSON.stringify(propertyName));
    } else {
      sessionStorage.setItem('property_name', '');
    }
    if (syncSource) {
      sessionStorage.setItem('sync_source', JSON.stringify(syncSource));
    } else {
      sessionStorage.setItem('sync_source', '');
    }
  }, [
    paid,
    onlyOTC,
    preferenceTime,
    date,
    priority,
    attemptEnd,
    attemptStart,
    landlord,
    propertyAddress,
    propertyName,
    syncSource,
  ]);

  const {data, refetch, isLoading} = useQuery(
    [
      'requests',
      String(currentPage - 1 || 0),
      order,
      status,
      currentUser?.id,
      keyWord,
      source,
      date,
      location,
      preferenceTime,
      onlyOTC,
      paid,
      priority,
      createdDate,
      attemptStart,
      attemptEnd,
      landlord,
      propertyName,
      propertyAddress,
      syncSource,
    ],
    getRequests
  );
  const {data: landlordList} = useQuery(['landlords'], getLandlordList, {
    enabled: hasFeature('hasManagedPropertiesOnCompanyDetails'),
  });
  const landlordSelectOptions = useMemo(() => {
    if (!landlordList) return [];
    return landlordList?.results?.map((lan) => ({
      label: `${lan?.first_name} ${lan?.last_name}`,
      value: lan?.id,
    }));
  }, [landlordList]);
  const {data: properties} = useQuery(
    ['properties', currentPage - 1],
    getCompanyProperties,
    {enabled: hasFeature('hasManagedPropertiesOnCompanyDetails')}
  );

  const {propertyNameOptions, propertyAddressOptions} = useMemo(() => {
    const options: {
      propertyNameOptions: SelectOption[];
      propertyAddressOptions: SelectOption[];
    } = {
      propertyNameOptions: [],
      propertyAddressOptions: [],
    };
    if (!properties || !properties?.results?.length) return options;
    properties?.results?.forEach((prop) => {
      const nameOption: SelectOption = {
        label: prop?.name,
        value: prop?.name,
      };
      const addressOption: SelectOption = {
        label: prop?.address?.display_address,
        value: prop?.id,
      };
      options.propertyNameOptions.push(nameOption);
      options.propertyAddressOptions.push(addressOption);
    });
    return options;
  }, [properties]);

  const getClients = async (signal: AbortSignal) => {
    try {
      const params = {
        limit: 150,
      };

      const {data} = await api.get<ListResponse<Customer>>(`/customers/`, {
        params,
        signal,
      });
      setClients(data.results);
    } catch (error) {}
  };

  const getProducts = async (signal: AbortSignal) => {
    try {
      const {data} = await api.get<ListResponse<Product>>(`/products/`, {
        params: {
          limit: 150,
        },
        signal,
      });
      const prs = data?.results?.filter((i) => i.offering_type === 'product');
      const srv = data?.results?.filter((i) => i.offering_type === 'service');
      setProducts(prs);
      setServices(srv);
    } catch (error) {}
  };

  const getAddress = async (id: number) => {
    setAddresses([]);
    try {
      const {data} = await api.get<Customer>(`/customers/${id}/`);
      setAddresses(data.addresses || []);
    } catch (error) {}
  };

  const onChangeTab = (val: any) => {
    if (val) {
      const newTabs = MMTable.some((item) => item.label === val.label)
        ? reject(MMTable, val)
        : [...MMTable, val];

      setMMTable(sortLodash(newTabs, 'index'));

      localStorage.setItem(
        'tableData',
        JSON.stringify(sortLodash(newTabs, 'index'))
      );
    }
  };

  const onChangeData = (val: any) => {
    setDate(val);
  };

  const onChangeLocation = (value: any) => {
    if (value) {
      setLocation((prev) =>
        prev.some((item) => item.label === value.label)
          ? reject(prev, value)
          : [...prev, value]
      );
    } else {
      setLocation([]);
    }
  };

  const handleNavigateRequest = useCallback(
    (row: DataItem) => {
      if (isPickupDropoff) {
        const orderType =
          data?.results?.find((i) => i.id === row.id)?.order_type || 'dropoff';
        history.push(
          `/orders/${row.sequential_order_number}?order_type=${orderType}`
        );
      } else {
        history.push(`/orders/${row.sequential_order_number}`);
      }
    },
    [history, isPickupDropoff, data?.results]
  );
  const onSubmit = () => {
    setSubmitting(true);
    const body = {
      customer_id: userId,
      address_id: addressId,
      drugs: [productIds, serviceIds].flat(),
    } as any;
    if (jobDescription && hasFeature('hasJobDescriptionOnOrderCreation')) {
      body.description = jobDescription;
    }
    if (isPickupDropoff) {
      body.order_type = selectedOrderType;
    }
    api
      .post(
        source === 'app' ? 'requests/create/' : 'requests/create-order/',
        body
      )
      .then(({data}) => {
        refetch();
        setUserId(0);
        setAddressId(0);
        setProductIds([]);
        setServiceIds([]);
        setVisible(false);
        handleNavigateRequest(data);
      })
      .catch((err) => {
        toast.error(err.message);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleClose = () => {
    setVisible(false);
    setUserId(0);
    setAddressId(0);
    setProductIds([]);
    setServiceIds([]);
  };

  const resetFilter = () => {
    setFilterVisible(false);
    const data = {
      location: [],
      priority: undefined,
      onlyOTC: undefined,
      paid: undefined,
      date: undefined,
      preferenceTime: undefined,

      createdDate: {
        start: undefined,
        end: undefined,
      },
      landlord: undefined,
      property_address: undefined,
      property_name: undefined,
      sync_source: undefined,
    };
    setFilter(data);
    filterHandler(data);
  };

  const filterHandler = (filter: any) => {
    setLocation(filter.location);
    setPriority(filter.priority);
    setPaid(filter.paid);
    setDate(filter.date);
    setOnlyOTC(filter.onlyOTC);
    setPreferenceTime(filter.preferenceTime);
    setCreatedDate(filter.createdDate);
    setAttemptStart(filter.attempt_start);
    setAttemptEnd(filter.attempt_end);
    setPropertyAddress(filter.property_address);
    setPropertyName(filter.property_name);
    setLandlord(filter.landlord);
    setSyncSource(filter.sync_source);
  };

  const applyFilter = () => {
    setFilterVisible(false);
    filterHandler(filter);
    history.push('/orders/page/1');
  };

  const handleSelectAttempt = (
    option: SingleValue<SelectOption<string | number, React.ReactNode>>
  ) => {
    if (option?.value === 5) {
      setFilter((prev: any) => ({
        ...prev,
        attempt_start: option,
        attempt_end: undefined,
      }));
    } else {
      setFilter((prev: any) => ({
        ...prev,
        attempt_start: option,
        attempt_end: option,
      }));
    }
  };
  const {
    mutateAsync: submitOrderStatusesMutation,
    isLoading: isSubmittingOrderStatuses,
  } = useMutation({
    mutationFn: submitOrderStatusesAsync,
    onSuccess: () => {
      refetch();
      setSelectedOrders([]);
      setShowStatusModal(false);
      setOrderStatus('ready_for_dispatch');
      setStatusesData({
        preference_date: null,
        preference_time: null,
        priority: null,
      });
      if (orderStatus === 'ready_for_dispatch') {
        history.push('/deliveries/delivery_planner');
        toast.success('Successfully prepared for dispatch');
      } else {
        toast.success('Successfully updated statuses');
      }
    },
    onError: (error) => {
      toast.error(
        JSON.stringify(get(error, 'response.data')) ||
          'Something wrong with creation. Try again later !'
      );
    },
  });
  const submitOrderStatuses = async () => {
    await submitOrderStatusesMutation({
      request_ids: selectedOrders,
      status: orderStatus,
      preference_date: statusesData.preference_date
        ? format(statusesData.preference_date, 'yyyy-MM-dd')
        : undefined,
      preference_time: statusesData.preference_time || undefined,
      priority: statusesData.priority || undefined,
    });
  };

  const mergeOrders = async () => {
    if (selectedOrders.length !== 2) {
      toast.error('Please select 2 orders to merge');
      return;
    }
    const [source_request_id, target_request_id] = selectedOrders;
    if (!source_request_id || !target_request_id) {
      toast.error('Please select 2 orders to merge');
      return;
    }
    const sourceOrderStatus = get(
      data?.results?.find((i) => i.id === source_request_id),
      'status',
      ''
    );
    const targetOrderStatus = get(
      data?.results?.find((i) => i.id === target_request_id),
      'status',
      ''
    );
    if (sourceOrderStatus !== 'in_progress' && sourceOrderStatus !== 'new') {
      toast.error('The status of the source order must be in progress or new');
      return;
    }
    if (targetOrderStatus !== 'in_progress' && targetOrderStatus !== 'new') {
      toast.error('The status of the target order must be in progress or new');
      return;
    }
    try {
      setIsMerging(true);
      await api.post('/requests/merge/', {
        source_request_id,
        target_request_id,
      });
      setSelectedOrders([]);
      toast.success('Orders merged successfully');
      refetch();
    } catch (error) {
      console.log(error);
      const message = get(
        error,
        'response.data.non_field_errors[0]',
        'Something wrong with merge. Try again later !'
      );
      toast.error(message);

      // const message = get(
      //   error,
      //   'response.data',
      //   'Something wrong with merge. Try again later !'
      // );
      // toast.error(message);
    } finally {
      setIsMerging(false);
    }
  };

  const tabs = useMemo(function () {
    return [
      {title: 'All', value: ''},
      {title: 'New', value: 'new'},
      {title: 'In-Progress', value: 'in_progress'},
      {title: 'Closed', value: 'completed'},
      {title: 'Invalid', value: 'invalid'},
      {title: 'Incomplete', value: 'incomplete'},
      {title: 'Call Doctor', value: 'call_doctor'},
    ];
  }, []);

  const MMtabs = useMemo(function () {
    return [
      {title: 'All', value: ''},
      {title: 'New', value: 'new'},
      {title: 'In-Progress', value: 'in_progress'},
      {title: 'Ready for Dispatch', value: 'ready_for_dispatch'},
      {title: 'Completed', value: 'completed'},
      {title: 'Skipped', value: 'canceled'},
      {title: 'Archived', value: 'archived'},
    ];
  }, []);

  const filtered = useMemo(() => {
    return (
      (location.length > 0 ||
        !!date ||
        !!createdDate?.start ||
        !!preferenceTime ||
        onlyOTC !== undefined ||
        priority !== undefined ||
        attemptStart !== undefined ||
        attemptEnd !== undefined ||
        paid !== undefined ||
        landlord !== undefined ||
        propertyAddress !== undefined ||
        propertyName !== undefined ||
        syncSource !== undefined) &&
      !filterVisible
    );
  }, [
    attemptEnd,
    attemptStart,
    createdDate?.start,
    date,
    filterVisible,
    landlord,
    location.length,
    onlyOTC,
    paid,
    preferenceTime,
    priority,
    propertyAddress,
    propertyName,
    syncSource,
  ]);
  const onFilterModalClose = () => {
    setFilterVisible(false);
  };
  return {
    data: data && !isLoading ? transformData(data.results, source) : [],
    totalPages:
      data?.count && data.limit ? Math.ceil(data.count / data.limit) : 0,
    dailyCount: data?.dailyCount,
    loading: isLoading,
    sortBy,
    sortOrder,
    tabs,
    MMtabs,
    status,
    keyWord: key,
    setKeyWord,
    onSort,
    setStatus,
    source,
    setDate,
    date,
    onChangeData,
    onChangeLocation,
    location,
    preferenceTime,
    onlyOTC,
    paid,
    priority,
    MMTable,
    clients,
    products,
    services,
    setProducts,
    setServices,
    setClients,
    addresses,
    getAddress,
    setAddresses,
    currentPage,
    setCurrentPage,
    params,
    onChangeTab,
    handleNavigateRequest,
    history,
    submitting,
    onSubmit,
    visible,
    setVisible,
    productIds,
    serviceIds,
    setProductIds,
    setServiceIds,
    userId,
    setUserId,
    addressId,
    setAddressId,
    handleClose,
    filter,
    setFilter,
    filterVisible,
    setFilterVisible,
    filtered,
    applyFilter,
    resetFilter,
    isModalOpen,
    setModalOpen,
    addressVisible,
    setAddressVisible,
    productVisible,
    setProductVisible,
    serviceVisible,
    setServiceVisible,
    ORDERS_SCHEME: isPickupDropoff
      ? PICKUP_DROPOFF_ORDERS_SCHEME
      : DEFAULT_ORDERS_SCHEME,
    selectedOrders,
    showStatusModal,
    setShowStatusModal,
    orderStatus,
    setOrderStatus,
    statusesData,
    setStatusesData,
    submitOrderStatuses,
    onFilterModalClose,
    handleSelectAttempt,
    landlordSelectOptions,
    propertyAddressOptions,
    propertyNameOptions,
    jobDescription,
    setJobDescription,
    mergeOrders,
    isMerging,
    isSubmittingOrderStatuses,
    selectedOrderType,
    setSelectedOrderType,
  };
}

export default useRequests;
